/**
 * Página de Embarcaciones — moderna.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Box, Typography, Button, Chip, IconButton, TextField, Switch, Tooltip, Avatar, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import BuildIcon from '@mui/icons-material/Build';
import AddTaskIcon from '@mui/icons-material/AddTask';
import DataTable from '../common/DataTable';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';
import GaleriaImagenes from '../common/GaleriaImagenes';
import MantenimientoSlideOver from './MantenimientoSlideOver';
import NuevoMantenimientoDialog from './NuevoMantenimientoDialog';
import EmbarcacionDetailPanel from './EmbarcacionDetailPanel';
import { useMantenimientos } from './shared/useFlota';
import { getUserRole } from './shared/format';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const getEmpresaId = () => { try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; } };
const getAuthHeaders = () => { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };

const emptyForm = {
  nombre: '', tipo: '', capacidad_total: '', capacidad_minima: '1', drive_url: '',
  modelo: '', astillero: '', año: '',
  eslora_pies: '', eslora_m: '', manga_m: '', calado_m: '',
  material_casco: '', capacidad_combustible_l: '', velocidad_max_nudos: '', velocidad_crucero_nudos: '',
  // Migración 077
  capacidad_clima_adverso: '',
  caracteristicas_visuales: '',
  sistema_audio: '', radio_comunicacion: '',
  tiene_gps: false, tiene_radio_vhf: false,
  chalecos_salvavidas_cantidad: '', chalecos_salvavidas_ninos_cantidad: '',
  amenidades: [],
  // Migración 117 · Combustible
  consumo_galones_por_hora: '', horas_motor_actuales: '',
};
const TIPOS = ['Bote deportivo', 'Lancha rápida', 'Catamarán', 'Yate', 'Velero', 'Bote', 'Otro'];
const MATERIALES_CASCO = [
  { v: 'fibra_vidrio', label: 'Fibra de vidrio' },
  { v: 'aluminio',     label: 'Aluminio' },
  { v: 'madera',       label: 'Madera' },
  { v: 'acero',        label: 'Acero' },
  { v: 'plastico',     label: 'Plástico/PVC' },
  { v: 'otro',         label: 'Otro' },
];
const CATALOGO_AMENIDADES = [
  { v: 'baño',           label: 'Baño',                icon: '🚽' },
  { v: 'asoleadora',     label: 'Asoleadoras',          icon: '☀️' },
  { v: 'techo',          label: 'Techo / Toldo',        icon: '⛱️' },
  { v: 'escalera_baño',  label: 'Escalera de baño',     icon: '🪜' },
  { v: 'ducha',          label: 'Ducha',                icon: '🚿' },
  { v: 'bar',            label: 'Bar',                  icon: '🍹' },
  { v: 'nevera',         label: 'Nevera',               icon: '🧊' },
  { v: 'cocina',         label: 'Cocina',               icon: '🍳' },
  { v: 'cama',           label: 'Cama / Camarote',      icon: '🛏️' },
  { v: 'tv',             label: 'TV',                   icon: '📺' },
];

export default function EmbarcacionesPage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();
  const [items, setItems] = useState([]);
  const [embarcacionesConAlerta, setEmbarcacionesConAlerta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bitacoraEmb, setBitacoraEmb] = useState(null);    // embarcación cuya bitácora se muestra
  const [mantDetalleId, setMantDetalleId] = useState(null); // mantenimiento abierto desde la bitácora
  const [nuevoMantOpen, setNuevoMantOpen] = useState(false);
  const { mantenimientos, refetch: refetchMantenimientos } = useMantenimientos();
  const userRole = getUserRole();
  const isComercial = false;  // Comerciales tienen acceso completo a Flota
  const [search, setSearch] = useState('');
  const [filterActivo, setFilterActivo] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [slideOpen, setSlideOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}&solo_activas=false`, { headers: getAuthHeaders() });
      setItems(await r.json());
    } catch { toast.error('Error al cargar'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  // Cargar lista de embarcaciones con alerta de mantenimiento vencido
  useEffect(() => {
    fetch(`${API_BASE}/turismo/mantenimiento/kpis?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setEmbarcacionesConAlerta(d?.embarcaciones_con_alerta || []))
      .catch(() => {});
  }, [empresaId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (q && !(i.nombre || '').toLowerCase().includes(q)) return false;
      if (filterActivo === 'activos' && !i.activa) return false;
      if (filterActivo === 'inactivos' && i.activa) return false;
      if (filterTipo && i.tipo !== filterTipo) return false;
      return true;
    });
  }, [items, search, filterActivo, filterTipo]);

  const handleNew = () => { setEditItem(null); setForm(emptyForm); setSlideOpen(true); };
  const handleEdit = (r) => {
    setEditItem(r);
    setForm({
      nombre: r.nombre,
      tipo: r.tipo || '',
      capacidad_total: String(r.capacidad_total),
      capacidad_minima: String(r.capacidad_minima || 1),
      drive_url: r.drive_url || '',
      modelo: r.modelo || '',
      astillero: r.astillero || '',
      año: r.año != null ? String(r.año) : '',
      eslora_pies: r.eslora_pies != null ? String(r.eslora_pies) : '',
      eslora_m: r.eslora_m != null ? String(r.eslora_m) : '',
      manga_m: r.manga_m != null ? String(r.manga_m) : '',
      calado_m: r.calado_m != null ? String(r.calado_m) : '',
      material_casco: r.material_casco || '',
      capacidad_combustible_l: r.capacidad_combustible_l != null ? String(r.capacidad_combustible_l) : '',
      velocidad_max_nudos: r.velocidad_max_nudos != null ? String(r.velocidad_max_nudos) : '',
      velocidad_crucero_nudos: r.velocidad_crucero_nudos != null ? String(r.velocidad_crucero_nudos) : '',
      // 117 · Combustible
      consumo_galones_por_hora: r.consumo_galones_por_hora != null ? String(r.consumo_galones_por_hora) : '',
      horas_motor_actuales: r.horas_motor_actuales != null ? String(r.horas_motor_actuales) : '',
      // 077
      capacidad_clima_adverso: r.capacidad_clima_adverso != null ? String(r.capacidad_clima_adverso) : '',
      caracteristicas_visuales: r.caracteristicas_visuales || '',
      sistema_audio: r.sistema_audio || '',
      radio_comunicacion: r.radio_comunicacion || '',
      tiene_gps: !!r.tiene_gps,
      tiene_radio_vhf: !!r.tiene_radio_vhf,
      chalecos_salvavidas_cantidad: r.chalecos_salvavidas_cantidad != null ? String(r.chalecos_salvavidas_cantidad) : '',
      chalecos_salvavidas_ninos_cantidad: r.chalecos_salvavidas_ninos_cantidad != null ? String(r.chalecos_salvavidas_ninos_cantidad) : '',
      amenidades: Array.isArray(r.amenidades) ? r.amenidades : [],
    });
    setSlideOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return toast.error('Falta nombre');
    if (!form.capacidad_total) return toast.error('Falta capacidad');
    setSaving(true);
    try {
      const _num = v => (v === '' || v == null) ? null : Number(v);
      const _int = v => (v === '' || v == null) ? null : parseInt(v);
      const body = {
        ...form,
        capacidad_total: parseInt(form.capacidad_total),
        capacidad_minima: parseInt(form.capacidad_minima) || 1,
        drive_url: form.drive_url?.trim() || null,
        // Specs técnicas (migración 076)
        modelo: form.modelo?.trim() || null,
        astillero: form.astillero?.trim() || null,
        año: _int(form.año),
        eslora_pies: _num(form.eslora_pies),
        eslora_m: _num(form.eslora_m),  // el backend recalcula si llega solo pies
        manga_m: _num(form.manga_m),
        calado_m: _num(form.calado_m),
        material_casco: form.material_casco || null,
        capacidad_combustible_l: _num(form.capacidad_combustible_l),
        velocidad_max_nudos: _num(form.velocidad_max_nudos),
        velocidad_crucero_nudos: _num(form.velocidad_crucero_nudos),
        // 117 · Combustible
        consumo_galones_por_hora: _num(form.consumo_galones_por_hora),
        horas_motor_actuales: _num(form.horas_motor_actuales),
        // 077
        capacidad_clima_adverso: _int(form.capacidad_clima_adverso),
        caracteristicas_visuales: form.caracteristicas_visuales?.trim() || null,
        sistema_audio: form.sistema_audio?.trim() || null,
        radio_comunicacion: form.radio_comunicacion?.trim() || null,
        tiene_gps: !!form.tiene_gps,
        tiene_radio_vhf: !!form.tiene_radio_vhf,
        chalecos_salvavidas_cantidad: _int(form.chalecos_salvavidas_cantidad),
        chalecos_salvavidas_ninos_cantidad: _int(form.chalecos_salvavidas_ninos_cantidad),
        amenidades: Array.isArray(form.amenidades) ? form.amenidades : [],
      };
      if (!editItem) body.empresa_id = empresaId;
      const url = editItem ? `${API_BASE}/turismo/embarcaciones/${editItem.id}` : `${API_BASE}/turismo/embarcaciones`;
      const r = await fetch(url, { method: editItem ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success(editItem ? 'Embarcación actualizada' : 'Embarcación creada');
      setSlideOpen(false); cargar(); refreshCounts?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleToggle = async (row, v) => {
    setItems(xs => xs.map(x => x.id === row.id ? { ...x, activa: v } : x));
    try {
      const r = await fetch(`${API_BASE}/turismo/embarcaciones/${row.id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activa: v }) });
      if (!r.ok) throw new Error();
      toast.success(v ? 'Activada' : 'Desactivada'); refreshCounts?.();
    } catch { setItems(xs => xs.map(x => x.id === row.id ? { ...x, activa: !v } : x)); toast.error('Error'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/embarcaciones/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!r.ok) throw new Error('No se pudo eliminar');
      toast.success('Eliminada'); setDeleteTarget(null); cargar(); refreshCounts?.();
    } catch (e) { toast.error(e.message); }
  };

  const handleBulkDelete = async () => {
    if (!deletingBulk) return;
    try {
      await Promise.all(deletingBulk.ids.map(id => fetch(`${API_BASE}/turismo/embarcaciones/${id}`, { method: 'DELETE', headers: getAuthHeaders() })));
      toast.success(`${deletingBulk.ids.length} eliminada(s)`); deletingBulk.run(); setDeletingBulk(null); cargar(); refreshCounts?.();
    } catch { toast.error('Error'); }
  };

  const handleBulkToggle = async (ids, activa, clear) => {
    try {
      await Promise.all(ids.map(id => fetch(`${API_BASE}/turismo/embarcaciones/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activa }) })));
      toast.success(`${ids.length} actualizada(s)`); clear(); cargar(); refreshCounts?.();
    } catch { toast.error('Error'); }
  };

  const columns = [
    { key: 'nombre', label: 'Embarcación', sortable: true,
      render: r => {
        const tieneAlerta = embarcacionesConAlerta.includes(r.id);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{
              bgcolor: tieneAlerta ? '#fee2e2' : '#8b5cf615',
              color: tieneAlerta ? '#dc2626' : '#8b5cf6',
              width: 32, height: 32,
            }}><DirectionsBoatIcon sx={{ fontSize: 18 }} /></Avatar>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{r.nombre}</Typography>
              {tieneAlerta && (
                <Chip size="small" label="⚠ Mantenimiento vencido"
                  sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 10, height: 18 }} />
              )}
            </Box>
          </Box>
        );
      } },
    { key: 'tipo', label: 'Tipo', sortable: true, render: r => r.tipo || '—' },
    { key: 'capacidad_total', label: 'Capacidad', sortable: true, align: 'center',
      render: r => <Chip size="small" label={`${r.capacidad_minima || 1} – ${r.capacidad_total} pax`} sx={{ height: 20, fontSize: 11 }} /> },
    { key: 'fotos', label: 'Fotos', align: 'center',
      sortValue: r => r.imagenes_count || 0,
      render: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
          {r.imagen_principal_url ? (
            <Box sx={{ width: 36, height: 36, borderRadius: 1, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <img src={`${API_BASE}${r.imagen_principal_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
          ) : (
            <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}>
              <PhotoLibraryIcon sx={{ fontSize: 18 }} />
            </Box>
          )}
          <Typography sx={{ fontSize: 11, color: r.imagenes_count > 0 ? '#0d9488' : '#94a3b8', fontWeight: 600 }}>
            {r.imagenes_count || 0}
          </Typography>
        </Box>
      ) },
    { key: 'activa', label: 'Activa', align: 'center', sortable: true,
      render: r => isComercial
        ? <Chip size="small" label={r.activa ? 'Activa' : 'Inactiva'}
            sx={{ bgcolor: r.activa ? '#dcfce7' : '#e2e8f0', color: r.activa ? '#166534' : '#475569',
                  fontWeight: 700, fontSize: 10, height: 20 }} />
        : <Switch size="small" checked={!!r.activa} onClick={e => e.stopPropagation()} onChange={(_, v) => handleToggle(r, v)} /> },
    { key: 'acciones', label: '', align: 'right',
      render: r => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
          <Tooltip title={isComercial ? 'Ver detalle' : 'Ver bitácora'}>
            <IconButton size="small" onClick={() => setBitacoraEmb(r)} sx={{ color: '#0d9488' }}><BuildIcon sx={{ fontSize: 16 }} /></IconButton>
          </Tooltip>
          {!isComercial && (
            <>
              <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(r)} sx={{ color: '#8b5cf6' }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              <Tooltip title="Eliminar"><IconButton size="small" onClick={() => setDeleteTarget(r)} sx={{ color: '#dc2626' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            </>
          )}
        </Box>
      ) },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#134e4a', mb: 0.5 }}>Embarcaciones</Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>Lanchas, yates, catamaranes que operas. Se asignan a salidas programadas.</Typography>
      </Box>

      <DataTable columns={columns} rows={filtered} loading={loading} rowKey="id"
        onRowClick={isComercial ? (r) => setBitacoraEmb(r) : handleEdit}
        search={{ value: search, onChange: setSearch, placeholder: 'Buscar embarcación...' }}
        filters={[
          { key: 'tipo', label: 'Tipo', value: filterTipo, onChange: setFilterTipo, options: TIPOS.map(t => ({ value: t, label: t })) },
          { key: 'estado', label: 'Estado', value: filterActivo, onChange: setFilterActivo, options: [{ value: 'activos', label: 'Solo activas' }, { value: 'inactivos', label: 'Solo inactivas' }] },
        ]}
        bulkActions={isComercial ? [] : [
          { label: 'Activar', icon: <VisibilityIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => handleBulkToggle(ids, true, c) },
          { label: 'Desactivar', icon: <VisibilityOffIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => handleBulkToggle(ids, false, c) },
          { label: 'Eliminar', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => setDeletingBulk({ ids, run: c }) },
        ]}
        actions={isComercial ? null : <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={handleNew}
          sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>Nueva embarcación</Button>}
        emptyState={items.length === 0
          ? <EmptyState icon={<DirectionsBoatIcon />} title="Sin embarcaciones" description="Registra tus lanchas y botes." ctaLabel="Crear" onCta={handleNew} />
          : <EmptyState icon={<DirectionsBoatIcon />} title="Sin resultados" size="sm" />}
      />

      <SlideOver open={slideOpen} onClose={() => setSlideOpen(false)}
        title={editItem ? 'Editar Embarcación' : 'Nueva Embarcación'} icon={<DirectionsBoatIcon />}
        subtitle={editItem?.nombre || 'Lancha / yate / catamarán'}
        footer={<>
          <Button onClick={() => setSlideOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disableElevation disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>
            {saving ? 'Guardando...' : (editItem ? 'Guardar' : 'Crear')}
          </Button>
        </>}>
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Datos básicos
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <TextField label="Nombre *" size="small" fullWidth value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
          <TextField label="Tipo" size="small" select fullWidth value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
            <MenuItem value="">(sin tipo)</MenuItem>
            {TIPOS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="URL Google Drive (opcional)" size="small" fullWidth
            value={form.drive_url} onChange={e => setForm({ ...form, drive_url: e.target.value })} />
        </Box>

        {/* Capacidad */}
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Capacidad de pasajeros
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
            <TextField label="Mínima" size="small" type="number" value={form.capacidad_minima}
              onChange={e => setForm({ ...form, capacidad_minima: e.target.value })}
              helperText="Para operar" />
            <TextField label="Máxima certificada *" size="small" type="number" value={form.capacidad_total}
              onChange={e => setForm({ ...form, capacidad_total: e.target.value })}
              helperText="Capacidad DIMAR" />
            <TextField label="Época de viento" size="small" type="number" value={form.capacidad_clima_adverso}
              onChange={e => setForm({ ...form, capacidad_clima_adverso: e.target.value })}
              helperText="Capacidad reducida en clima adverso" />
          </Box>
        </Box>

        {/* Dimensiones técnicas */}
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Dimensiones técnicas
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 1.5 }}>
            <TextField label="Modelo" size="small" value={form.modelo}
              onChange={e => setForm({ ...form, modelo: e.target.value })}
              placeholder="Ej: Eduardoño 33" />
            <TextField label="Astillero / Fabricante" size="small" value={form.astillero}
              onChange={e => setForm({ ...form, astillero: e.target.value })} />
            <TextField label="Año" size="small" type="number" value={form.año}
              onChange={e => setForm({ ...form, año: e.target.value })} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
            <TextField label="Eslora (pies)" size="small" type="number" value={form.eslora_pies}
              onChange={e => setForm({ ...form, eslora_pies: e.target.value, eslora_m: '' })}
              inputProps={{ step: 0.1 }}
              helperText={form.eslora_pies ? `≈ ${(Number(form.eslora_pies) * 0.3048).toFixed(2)} m` : ' '} />
            <TextField label="Manga (m)" size="small" type="number" value={form.manga_m}
              onChange={e => setForm({ ...form, manga_m: e.target.value })}
              inputProps={{ step: 0.1 }} />
            <TextField label="Calado (m)" size="small" type="number" value={form.calado_m}
              onChange={e => setForm({ ...form, calado_m: e.target.value })}
              inputProps={{ step: 0.1 }} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField label="Material del casco" size="small" select value={form.material_casco}
              onChange={e => setForm({ ...form, material_casco: e.target.value })}>
              <MenuItem value="">—</MenuItem>
              {MATERIALES_CASCO.map(m => <MenuItem key={m.v} value={m.v}>{m.label}</MenuItem>)}
            </TextField>
            <TextField label="Capacidad combustible (L)" size="small" type="number"
              value={form.capacidad_combustible_l}
              onChange={e => setForm({ ...form, capacidad_combustible_l: e.target.value })} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField label="Velocidad máxima (nudos)" size="small" type="number" value={form.velocidad_max_nudos}
              onChange={e => setForm({ ...form, velocidad_max_nudos: e.target.value })}
              inputProps={{ step: 0.5 }} />
            <TextField label="Velocidad crucero (nudos)" size="small" type="number" value={form.velocidad_crucero_nudos}
              onChange={e => setForm({ ...form, velocidad_crucero_nudos: e.target.value })}
              inputProps={{ step: 0.5 }} />
          </Box>
          {/* Configuración del módulo Combustible (Fase 3) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, pt: 1, borderTop: '1px dashed #e2e8f0' }}>
            <TextField label="Consumo estándar (gal/hora)" size="small" type="number"
              value={form.consumo_galones_por_hora}
              onChange={e => setForm({ ...form, consumo_galones_por_hora: e.target.value })}
              inputProps={{ step: 0.1, min: 0 }}
              helperText="Para calcular consumo estimado por operación" />
            <TextField label="Horómetro actual (horas)" size="small" type="number"
              value={form.horas_motor_actuales}
              onChange={e => setForm({ ...form, horas_motor_actuales: e.target.value })}
              inputProps={{ step: 1, min: 0 }}
              helperText="Se auto-actualiza al registrar consumos" />
          </Box>
        </Box>

        {/* Amenidades (catálogo + checkboxes) */}
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Amenidades a bordo
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
          {CATALOGO_AMENIDADES.map(a => {
            const sel = form.amenidades?.includes(a.v);
            return (
              <Chip key={a.v} label={`${a.icon} ${a.label}`}
                onClick={() => setForm({ ...form,
                  amenidades: sel
                    ? form.amenidades.filter(x => x !== a.v)
                    : [...form.amenidades, a.v],
                })}
                sx={{
                  cursor: 'pointer', fontWeight: 600, fontSize: 12,
                  bgcolor: sel ? '#0d9488' : '#f1f5f9',
                  color: sel ? '#fff' : '#475569',
                  '&:hover': { bgcolor: sel ? '#0f766e' : '#e2e8f0' },
                }} />
            );
          })}
        </Box>

        {/* Equipamiento de audio y comunicación */}
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Audio y comunicación
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <TextField label="Sistema de audio" size="small" fullWidth value={form.sistema_audio}
            onChange={e => setForm({ ...form, sistema_audio: e.target.value })}
            placeholder="Ej: JL Audio + Bluetooth" />
          <TextField label="Radio comunicación" size="small" fullWidth value={form.radio_comunicacion}
            onChange={e => setForm({ ...form, radio_comunicacion: e.target.value })}
            placeholder="Ej: Clarion VHF" />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Switch size="small" checked={!!form.tiene_gps}
                onChange={e => setForm({ ...form, tiene_gps: e.target.checked })} />
              <Typography sx={{ fontSize: 13 }}>GPS</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Switch size="small" checked={!!form.tiene_radio_vhf}
                onChange={e => setForm({ ...form, tiene_radio_vhf: e.target.checked })} />
              <Typography sx={{ fontSize: 13 }}>Radio VHF</Typography>
            </Box>
          </Box>
        </Box>

        {/* Equipamiento de seguridad */}
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Seguridad
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3 }}>
          <TextField label="Chalecos salvavidas (adultos)" size="small" type="number"
            value={form.chalecos_salvavidas_cantidad}
            onChange={e => setForm({ ...form, chalecos_salvavidas_cantidad: e.target.value })}
            helperText="Cantidad disponible" />
          <TextField label="Chalecos salvavidas (niños)" size="small" type="number"
            value={form.chalecos_salvavidas_ninos_cantidad}
            onChange={e => setForm({ ...form, chalecos_salvavidas_ninos_cantidad: e.target.value })} />
        </Box>

        {/* Características visuales */}
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Características visuales
        </Typography>
        <Box sx={{ mb: 3 }}>
          <TextField multiline minRows={2} size="small" fullWidth
            placeholder="Ej: Cojinería beige, casco blanco, toldo azul..."
            value={form.caracteristicas_visuales}
            onChange={e => setForm({ ...form, caracteristicas_visuales: e.target.value })} />
        </Box>

        {/* Galería de imágenes */}
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          <PhotoLibraryIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />
          Galería de fotos
        </Typography>
        <GaleriaImagenes
          entidadId={editItem?.id}
          endpoint="/turismo/embarcaciones"
          color="#8b5cf6"
        />
      </SlideOver>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete}
        title="¿Eliminar esta embarcación?"
        message={deleteTarget ? `Vas a eliminar "${deleteTarget.nombre}".` : ''} confirmText="Eliminar" />
      <ConfirmDialog open={!!deletingBulk} onClose={() => setDeletingBulk(null)} onConfirm={handleBulkDelete}
        title={`¿Eliminar ${deletingBulk?.ids?.length || 0}?`} message="Esta acción no se puede deshacer." confirmText="Eliminar todas" />

      {/* SlideOver detalle de embarcación: tabs Resumen · Motores · Documentos · Mantenimientos */}
      {bitacoraEmb && (
        <SlideOver open onClose={() => setBitacoraEmb(null)} variant="wide"
          title={bitacoraEmb.nombre}
          subtitle="Detalle de la embarcación"
          icon={<BuildIcon />}
          footer={
            <Button onClick={() => setBitacoraEmb(null)} sx={{ textTransform: 'none' }}>Cerrar</Button>
          }>
          <EmbarcacionDetailPanel
            embarcacion={bitacoraEmb}
            mantenimientos={mantenimientos}
            onOpenMantenimiento={(id) => setMantDetalleId(id)}
            onProgramarMant={() => setNuevoMantOpen(true)}
            onRefetchMant={refetchMantenimientos}
          />
        </SlideOver>
      )}

      {mantDetalleId && (
        <MantenimientoSlideOver mantenimientoId={mantDetalleId}
          onClose={() => setMantDetalleId(null)}
          onUpdated={() => { refetchMantenimientos(); cargar(); }} />
      )}

      {nuevoMantOpen && bitacoraEmb && (
        <NuevoMantenimientoDialog
          open={nuevoMantOpen}
          embarcacionId={bitacoraEmb.id}
          onClose={() => setNuevoMantOpen(false)}
          onCreated={() => { setNuevoMantOpen(false); refetchMantenimientos(); }} />
      )}

      <ToastContainer />
    </Box>
  );
}
